import debounce from 'just-debounce-it';
import { createContext, provideContextRecord } from '../../base/context';
import { DisposalBin, listen, vdsEvent } from '../../base/events';
import { Logger, LogLevel, LogLevelNameMap } from '../../base/logger';
import { RequestQueue } from '../../base/queue';
import { DEV_MODE } from '../../global/env';
import { keysOf } from '../../utils/object';
import { isArray, isNil } from '../../utils/unit';
import { mediaContext } from '../context';
/* c8 ignore next */
const _logLevel = createContext(LogLevel.Silent);
/**
 * The media controller acts as a message bus between the media provider and all other
 * components, such as UI components and plugins. The main responsibilities are:
 *
 * - Provide the media context that is used to pass media state down to components (this
 * context is injected into and managed by the media provider).
 *
 * - Listen for media request events and fulfill them by calling the appropriate props/methods on
 * the current media provider.
 *
 * 💡 The base `MediaPlayer` acts as both a media controller and provider.
 */
export class MediaController {
    constructor(_host) {
        this._host = _host;
        // -------------------------------------------------------------------------------------------
        // Media Request Events
        // -------------------------------------------------------------------------------------------
        /**
         * Media requests that have been made but are waiting to be satisfied. Key represents the media
         * event type the request is waiting for to be considered "satisfied".
         */
        this._pendingMediaRequests = {
            play: [],
            pause: [],
            volume: [],
            fullscreen: [],
            seeked: [],
            seeking: []
        };
        this._isSeekingRequestPending = false;
        this._firingWaiting = false;
        this._fireWaiting = debounce(() => {
            if (this.mediaCtx.playing ||
                this._isSeekingRequestPending ||
                !this._lastWaitingEvent) {
                return;
            }
            this.mediaCtx.waiting = true;
            this._firingWaiting = true;
            const event = vdsEvent('vds-waiting', {
                originalEvent: this._lastWaitingEvent
            });
            this._host.dispatchEvent(event);
            this._firingWaiting = false;
            this._lastWaitingEvent = undefined;
        }, 300);
        /* c8 ignore next */
        this._logLevelProvider = _logLevel.provide(_host);
        /* c8 ignore start */
        if (DEV_MODE && !Logger._consumeLogLevel) {
            // Inject log level context into `Logger` to avoid dep cycle.
            Logger._consumeLogLevel = _logLevel.consume;
        }
        /* c8 ignore stop */
        this.mediaCtx = provideContextRecord(_host, mediaContext);
        /* c8 ignore next */
        this._logger = DEV_MODE && new Logger(_host, { owner: this });
        this._disconnectDisposal = new DisposalBin(_host, 
        /* c8 ignore next */
        DEV_MODE && { name: 'disconnectDisposal', owner: this });
        this._mediaProviderConnectedQueue = new RequestQueue(_host, 
        /* c8 ignore next */
        DEV_MODE && {
            name: 'mediaProviderConnectedQueue',
            owner: this
        });
        this._mediaProviderDisconnectedDisposal = new DisposalBin(_host, 
        /* c8 ignore next */
        DEV_MODE && { name: 'mediaProviderDisconnectDisposal', owner: this });
        _host.addController(this);
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    hostConnected() {
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logEvents();
            this._logErrors();
        }
        /* c8 ignore stop */
        this._addEventListeners();
    }
    hostDisconnected() {
        this._clearPendingMediaRequests();
        this._mediaProviderConnectedQueue.destroy();
        this._mediaProviderDisconnectedDisposal.empty();
        this._disconnectDisposal.empty();
    }
    _addEventListeners() {
        const eventListeners = {
            //
            'vds-media-provider-connect': this._handleMediaProviderConnect,
            'vds-mute-request': this._handleMuteRequest,
            'vds-unmute-request': this._handleUnmuteRequest,
            'vds-play-request': this._handlePlayRequest,
            'vds-pause-request': this._handlePauseRequest,
            'vds-seeking-request': this._handleSeekingRequest,
            'vds-seek-request': this._handleSeekRequest,
            'vds-volume-change-request': this._handleVolumeChangeRequest,
            'vds-enter-fullscreen-request': this._handleEnterFullscreenRequest,
            'vds-exit-fullscreen-request': this._handleExitFullscreenRequest,
            'vds-fullscreen-change': this._handleFullscreenChange,
            'vds-fullscreen-error': this._handleFullscreenError,
            //
            'vds-play': [this._handlePlay, { capture: true }],
            'vds-play-error': [this._handlePlayError, { capture: true }],
            'vds-playing': [this._handlePlaying, { capture: true }],
            'vds-pause': [this._handlePause, { capture: true }],
            'vds-volume-change': [this._handleVolumeChange, { capture: true }],
            'vds-replay': [this._handleReplay, { capture: true }],
            'vds-seeking': [this._handleSeeking, { capture: true }],
            'vds-seeked': [this._handleSeeked, { capture: true }],
            'vds-waiting': [this._handleWaiting],
            //
            seeked: [this._handleSeeked, { capture: true }]
        };
        keysOf(eventListeners).forEach((eventType) => {
            const eventListener = eventListeners[eventType];
            const listener = isArray(eventListener)
                ? eventListener[0]
                : eventListener;
            const options = isArray(eventListener) ? eventListener[1] : undefined;
            const dispose = listen(this._host, eventType, listener.bind(this), options);
            this._disconnectDisposal.add(dispose);
        });
    }
    // -------------------------------------------------------------------------------------------
    // Logging
    // -------------------------------------------------------------------------------------------
    get logLevel() {
        /* c8 ignore next */
        return DEV_MODE ? LogLevelNameMap[this._logLevelProvider.value] : 'silent';
    }
    set logLevel(newLevel) {
        /* c8 ignore next */
        const numericLevel = DEV_MODE
            ? Object.values(LogLevelNameMap).findIndex((l) => l === newLevel)
            : 0;
        this._logLevelProvider.value = numericLevel >= 0 ? numericLevel : 0;
    }
    _logEvents() {
        /* c8 ignore start */
        if (DEV_MODE) {
            const loggedEvents = [
                'vds-controls-change',
                'vds-fullscreen-change'
            ];
            loggedEvents.forEach((eventType) => {
                const dispose = listen(this._host, eventType, (event) => {
                    this._logger.infoGroup(`📡 dispatching \`${eventType}\``)
                        .appendWithLabel('Event', event)
                        .appendWithLabel('Provider', this.mediaProvider)
                        .end();
                });
                this._disconnectDisposal.add(dispose);
            });
        }
        /* c8 ignore stop */
    }
    _logErrors() {
        /* c8 ignore start */
        if (DEV_MODE) {
            const dispose = listen(this._host, 'vds-error', (event) => {
                this._logger.errorGroup(event.type)
                    .appendWithLabel('Context', this.mediaCtx)
                    .appendWithLabel('Event', event)
                    .appendWithLabel('Provider', this.mediaProvider)
                    .end();
            });
            this._disconnectDisposal.add(dispose);
        }
        /* c8 ignore stop */
    }
    get mediaProvider() {
        return this._mediaProvider;
    }
    /** @internal */
    setMediaProvider(mediaProvider) {
        this._mediaProvider = mediaProvider;
    }
    _handleMediaProviderConnect(event) {
        event.stopPropagation();
        const { element, onDisconnect } = event.detail;
        if (this.mediaProvider === element)
            return;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.infoGroup('media provider connected')
                .appendWithLabel('Provider', element)
                .end();
        }
        /* c8 ignore stop */
        this._handleMediaProviderDisconnect();
        this._mediaProvider = element;
        this._attachMediaContextRecordToProvider();
        this._flushMediaProviderConnectedQueue();
        onDisconnect(this._handleMediaProviderDisconnect.bind(this));
    }
    _handleMediaProviderDisconnect() {
        if (isNil(this.mediaProvider))
            return;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.infoGroup('media provider disconnected')
                .appendWithLabel('Provider', this.mediaProvider)
                .end();
        }
        /* c8 ignore stop */
        this._mediaProviderConnectedQueue.destroy();
        this._mediaProviderDisconnectedDisposal.empty();
        this._mediaProvider = undefined;
    }
    _flushMediaProviderConnectedQueue() {
        this._mediaProviderConnectedQueue.flush();
        this._mediaProviderConnectedQueue.serveImmediately = true;
        this._mediaProviderDisconnectedDisposal.add(() => {
            this._mediaProviderConnectedQueue.serveImmediately = false;
            this._mediaProviderConnectedQueue.reset();
        });
    }
    /**
     * A snapshot of the current media state.
     */
    get mediaState() {
        return Object.assign({}, this.mediaCtx);
    }
    _attachMediaContextRecordToProvider() {
        if (isNil(this.mediaProvider))
            return;
        // Copy over context values before setting on provider.
        Object.keys(this.mediaProvider.ctx).forEach((prop) => {
            this.mediaCtx[prop] = this.mediaProvider.ctx[prop];
        });
        this.mediaProvider.ctx.__destroy();
        // @ts-expect-error - Override readonly
        this.mediaProvider.ctx = this.mediaCtx;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.infoGroup('attached context record')
                .appendWithLabel('Provider', this.mediaProvider)
                .appendWithLabel('Context', this.mediaCtx)
                .end();
        }
        /* c8 ignore stop */
        this._mediaProviderDisconnectedDisposal.add(() => {
            const ctx = provideContextRecord(this.mediaProvider, mediaContext);
            // Copy over context values before setting on provider.
            Object.keys(this.mediaCtx).forEach((prop) => {
                ctx[prop] = this.mediaCtx[prop];
            });
            // @ts-expect-error - Override readonly
            this.mediaProvider.ctx = ctx;
        });
    }
    _clearPendingMediaRequests() {
        keysOf(this._pendingMediaRequests).forEach((key) => {
            this._pendingMediaRequests[key] = [];
        });
    }
    satisfyMediaRequest(type, event) {
        event.requestEvent = this._pendingMediaRequests[type].shift();
    }
    /**
     * Override this to allow media events to bubble up the DOM.
     *
     * @param event
     */
    _mediaRequestEventGateway(event) {
        event.stopPropagation();
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.infoGroup(`📬 received \`${event.type}\``)
                .appendWithLabel('Request', event)
                .end();
        }
        /* c8 ignore stop */
        return true;
    }
    _handleMuteRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('muted', () => {
            var _a;
            if (((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.muted) === true)
                return;
            this._pendingMediaRequests.volume.push(event);
            this.mediaProvider.muted = true;
        });
    }
    _handleUnmuteRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('muted', () => {
            var _a;
            if (((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.muted) === false)
                return;
            this._pendingMediaRequests.volume.push(event);
            this.mediaProvider.muted = false;
        });
    }
    _handlePlayRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('paused', () => {
            var _a;
            if (((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.paused) === false)
                return;
            this._pendingMediaRequests.play.push(event);
            this.mediaProvider.paused = false;
        });
    }
    _handlePauseRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('paused', () => {
            var _a;
            if (((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.paused) === true)
                return;
            this._pendingMediaRequests.pause.push(event);
            this.mediaProvider.paused = true;
        });
    }
    _handleSeekingRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('seeking', () => {
            this._pendingMediaRequests.seeking.push(event);
            this._isSeekingRequestPending = true;
            this.mediaProvider.currentTime = event.detail;
            this._fireWaiting.cancel();
        });
    }
    _handleSeekRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('seeking', () => {
            this._pendingMediaRequests.seeked.push(event);
            this._isSeekingRequestPending = false;
            let time = event.detail;
            // Snap to end if close enough.
            if (this.mediaProvider.duration - event.detail < 0.25) {
                time = this.mediaProvider.duration;
            }
            this.mediaProvider.currentTime = time;
        });
    }
    _handleVolumeChangeRequest(event) {
        if (!this._mediaRequestEventGateway(event))
            return;
        this._mediaProviderConnectedQueue.queue('volume', () => {
            var _a;
            if (((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.volume) === event.detail)
                return;
            this._pendingMediaRequests.volume.push(event);
            this.mediaProvider.volume = event.detail;
        });
    }
    async _handleEnterFullscreenRequest(event) {
        var _a;
        if (!this._mediaRequestEventGateway(event) ||
            ((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.fullscreen)) {
            return;
        }
        this._pendingMediaRequests.fullscreen.push(event);
        await this._host.requestFullscreen();
    }
    async _handleExitFullscreenRequest(event) {
        var _a, _b, _c;
        if (!this._mediaRequestEventGateway(event) ||
            !((_a = this._mediaProvider) === null || _a === void 0 ? void 0 : _a.fullscreen)) {
            return;
        }
        this._pendingMediaRequests.fullscreen.push(event);
        await ((_c = (_b = this._host).exitFullscreen) === null || _c === void 0 ? void 0 : _c.call(_b));
    }
    _handleFullscreenChange(event) {
        this.mediaCtx.fullscreen = event.detail;
        this.satisfyMediaRequest('fullscreen', event);
    }
    _handleFullscreenError(event) {
        this.satisfyMediaRequest('fullscreen', event);
    }
    // -------------------------------------------------------------------------------------------
    // Media Events
    // -------------------------------------------------------------------------------------------
    _handlePlay(event) {
        this.satisfyMediaRequest('play', event);
    }
    _handlePlayError(event) {
        this.satisfyMediaRequest('play', event);
    }
    _handlePlaying(event) {
        this._fireWaiting.cancel();
        if (this._isSeekingRequestPending) {
            event.stopImmediatePropagation();
            this.mediaCtx.seeking = true;
        }
    }
    _handlePause(event) {
        this.satisfyMediaRequest('pause', event);
        this._fireWaiting.cancel();
    }
    _handleVolumeChange(event) {
        this.satisfyMediaRequest('volume', event);
    }
    _handleReplay(event) {
        event.requestEvent = this._pendingMediaRequests.play[0];
    }
    _handleSeeking(event) {
        this.satisfyMediaRequest('seeking', event);
        if (this._lastWaitingEvent)
            this._fireWaiting();
    }
    _handleSeeked(event) {
        // We don't want `seeked` events firing while seeking is updating media playback position.
        if (this._isSeekingRequestPending) {
            event.stopImmediatePropagation();
            this.mediaCtx.seeking = true;
        }
        else if (event.type === 'vds-seeked') {
            this._fireWaiting.cancel();
            this.satisfyMediaRequest('seeked', event);
        }
    }
    _handleWaiting(event) {
        if (this._firingWaiting)
            return;
        event.stopImmediatePropagation();
        this.mediaCtx.waiting = false;
        this._lastWaitingEvent = event;
        this._fireWaiting();
    }
}
